import React, { useState, useEffect } from 'react';
import { shuffleArray } from '../utils/randomization';
import { generatePlaceholderSVG } from '../utils/placeholderGenerator';

function PostTaskCheck({ seenImages, onComplete, reviewerNav }) {
  const [checkImages, setCheckImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [responses, setResponses] = useState({
    recognition: [],
    sourceAttribution: []
  });
  const [phase, setPhase] = useState('recognition');
  const [buttonClicked, setButtonClicked] = useState({}); // Track which buttons were clicked
  
  // Use reviewer navigation if provided
  const displayIndex = reviewerNav?.postTaskIndex !== null && reviewerNav?.postTaskIndex !== undefined ? 
                       reviewerNav.postTaskIndex : currentImageIndex;
  const displayPhase = phase; // Always use actual phase, not reviewer navigation

  useEffect(() => {
    // Extract a subset of images from all blocks
    const allImages = [];
    
    // Check if we have actual trial data
    if (seenImages && seenImages.length > 0) {
      seenImages.forEach(block => {
        if (block.trials && block.trials.length > 0) {
          block.trials.forEach(trial => {
            // Add some images from trials (avoiding duplicates)
            if (!allImages.find(img => img.path === trial.leftImage)) {
              allImages.push({
                path: trial.leftImage,
                type: trial.leftType,
                artist: block.artist
              });
            }
            if (!allImages.find(img => img.path === trial.rightImage)) {
              allImages.push({
                path: trial.rightImage,
                type: trial.rightType,
                artist: block.artist
              });
            }
          });
        }
      });
    }
    
    // If no trial data (e.g., when using forward navigation), create mock data
    if (allImages.length === 0) {
      // Create some placeholder images for testing - ensure we have enough for all navigation options
      const mockArtists = ['van-gogh', 'monet', 'picasso', 'cezanne', 'rembrandt', 'warhol', 'hokusai', 'kahlo'];
      const mockTypes = ['real', 'ai-typical', 'ai-plausible', 'ai-anachronistic'];
      
      // Create at least 30 images to match the subset size
      for (let i = 0; i < 30; i++) {
        const artist = mockArtists[i % mockArtists.length];
        const type = mockTypes[i % mockTypes.length];
        allImages.push({
          path: `/images/${type === 'real' ? 'real' : 'ai/midjourney'}/${artist}/test${i}.jpg`,
          type: type,
          artist: artist
        });
      }
    }
    
    // Select a random subset of images
    const shuffled = shuffleArray(allImages);
    const subset = shuffled.slice(0, Math.min(30, shuffled.length));
    setCheckImages(subset);
  }, [seenImages]);

  const handleRecognition = (recognized) => {
    try {
      // Use displayIndex when in navigation mode, otherwise currentImageIndex
      const activeIndex = reviewerNav?.postTaskIndex !== null && reviewerNav?.postTaskIndex !== undefined ? 
                         reviewerNav.postTaskIndex : currentImageIndex;
      
      // Safety check
      if (!checkImages[activeIndex]) {
        console.error('No image at index:', activeIndex, 'checkImages length:', checkImages.length);
        return;
      }
      
      const newResponse = {
        image: checkImages[activeIndex].path,
        type: checkImages[activeIndex].type,
        artist: checkImages[activeIndex].artist,
        recognized
      };
      
      setResponses(prev => ({
        ...prev,
        recognition: [...prev.recognition, newResponse]
      }));
      
      // Visual feedback
      setButtonClicked(prev => ({
        ...prev,
        [`recognition_${activeIndex}`]: recognized ? 'yes' : 'no'
      }));
      
      // Always advance to next question after a brief delay for visual feedback
      setTimeout(() => {
        if (activeIndex < checkImages.length - 1) {
          // If in navigation mode, update the index there too
          if (reviewerNav?.postTaskIndex !== null && reviewerNav?.postTaskIndex !== undefined) {
            // We need to tell the parent to update navigation
            setCurrentImageIndex(activeIndex + 1);
            // Also update display for navigation mode
            if (reviewerNav.onIndexChange) {
              reviewerNav.onIndexChange(activeIndex + 1);
            }
          } else {
            setCurrentImageIndex(activeIndex + 1);
          }
        } else {
          setPhase('attribution');
          setCurrentImageIndex(0);
          if (reviewerNav?.onPhaseChange) {
            reviewerNav.onPhaseChange('attribution', 0);
          }
        }
      }, 300); // Brief delay to show the feedback
    } catch (error) {
      console.error('Error in handleRecognition:', error);
    }
  };

  const handleAttribution = (attribution) => {
    try {
      // Use displayIndex when in navigation mode, otherwise currentImageIndex
      const activeIndex = reviewerNav?.postTaskIndex !== null && reviewerNav?.postTaskIndex !== undefined ? 
                         reviewerNav.postTaskIndex : currentImageIndex;
      
      // Safety check
      if (!checkImages[activeIndex]) {
        console.error('No image at index:', activeIndex, 'checkImages length:', checkImages.length);
        return;
      }
      
      const newResponse = {
        image: checkImages[activeIndex].path,
        type: checkImages[activeIndex].type,
        artist: checkImages[activeIndex].artist,
        attributedTo: attribution
      };
      
      // Update responses with the current ones plus the new one
      const updatedResponses = {
        ...responses,
        sourceAttribution: [...responses.sourceAttribution, newResponse]
      };
      setResponses(updatedResponses);
      
      // Visual feedback
      setButtonClicked(prev => ({
        ...prev,
        [`attribution_${activeIndex}`]: attribution
      }));
      
      // Always advance after a brief delay for visual feedback
      setTimeout(() => {
        if (activeIndex < checkImages.length - 1) {
          setCurrentImageIndex(activeIndex + 1);
        } else {
          onComplete(updatedResponses);
        }
      }, 300); // Brief delay to show the feedback
    } catch (error) {
      console.error('Error in handleAttribution:', error);
    }
  };

  if (checkImages.length === 0) {
    return <div className="phase-container">Loading post-task checks...</div>;
  }

  const currentImage = checkImages[displayIndex] || checkImages[0];
  const progress = ((displayIndex + 1) / checkImages.length) * 100;

  // Generate descriptive placeholder for post-task checks with type labels
  const getImageSrc = (imageData) => {
    if (!imageData) return generatePlaceholderSVG('Test Image', 300, 300, '#7788aa', 'POST-TASK CHECK');
    
    const { path, type, artist } = imageData;
    if (path && path.startsWith('http')) return path;
    
    // Create descriptive text based on image type and artist
    let description = '';
    let typeLabel = '';
    const artistName = artist ? artist.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Artist';
    
    if (type === 'real') {
      description = `Real ${artistName} Painting`;
      typeLabel = 'REAL PAINTING - RECOGNITION CHECK';
    } else if (type === 'ai-typical') {
      description = `AI Generated: Typical ${artistName} Subject`;
      typeLabel = 'AI-GENERATED: TYPICAL SUBJECT';
    } else if (type === 'ai-plausible') {
      description = `AI Generated: Plausible ${artistName} Subject`;
      typeLabel = 'AI-GENERATED: ATYPICAL BUT PLAUSIBLE';
    } else if (type === 'ai-anachronistic') {
      description = `AI Generated: Anachronistic ${artistName} Style`;
      typeLabel = 'AI-GENERATED: ATYPICAL & ANACHRONISTIC';
    } else {
      description = `${artistName} Style Image`;
      typeLabel = 'POST-TASK CHECK';
    }
    
    return generatePlaceholderSVG(description, 300, 300, '#7788aa', typeLabel);
  };

  return (
    <div className="phase-container">
      <h1>Post-Task Questions</h1>
      
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }} />
      </div>
      
      <p style={{ textAlign: 'center', marginBottom: '20px' }}>
        Image {displayIndex + 1} of {checkImages.length}
      </p>
      
      <div className="post-task-item">
        <img 
          className="post-task-image"
          src={getImageSrc(currentImage)}
          alt="Recognition check"
        />
        
        {displayPhase === 'recognition' && (
          <>
            <div className="question">
              Have you ever seen this specific image before this study?
            </div>
            <div className="button-group">
              <button 
                onClick={() => handleRecognition(true)}
                style={{
                  background: buttonClicked[`recognition_${displayIndex}`] === 'yes' ? '#28a745' : '#0056b3'
                }}
              >
                Yes
              </button>
              <button 
                onClick={() => handleRecognition(false)}
                style={{
                  background: buttonClicked[`recognition_${displayIndex}`] === 'no' ? '#28a745' : '#0056b3'
                }}
              >
                No
              </button>
            </div>
          </>
        )}
        
        {displayPhase === 'attribution' && (
          <>
            <div className="question">
              Do you believe this image was made by a human artist or by an artificial intelligence system?
            </div>
            <div className="button-group">
              <button 
                onClick={() => handleAttribution('human')}
                style={{
                  background: buttonClicked[`attribution_${displayIndex}`] === 'human' ? '#28a745' : '#0056b3'
                }}
              >
                Human artist
              </button>
              <button 
                onClick={() => handleAttribution('ai')}
                style={{
                  background: buttonClicked[`attribution_${displayIndex}`] === 'ai' ? '#28a745' : '#0056b3'
                }}
              >
                Artificial intelligence
              </button>
              <button 
                onClick={() => handleAttribution('unsure')}
                style={{
                  background: buttonClicked[`attribution_${displayIndex}`] === 'unsure' ? '#28a745' : '#0056b3'
                }}
              >
                I don't know
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PostTaskCheck;